import React, { useCallback, useEffect, useState, Component } from 'react';
import { Activity, ShieldCheck, Sliders, Table2, RefreshCw, AlertTriangle, Camera, GitCompare, GraduationCap, BookOpenCheck } from 'lucide-react';
import { PageLayout } from '../../shared/PageLayout';
import { AdminTabs } from '../shared/AdminTabs';
import { ConnectionBadge } from '../shared/ConnectionBadge';
import { Button } from '../../../components/ui/Button';
import { RulesTab, GovernanceSettingsTab, useGovernanceData, OverviewDashboard, QuestionsTab, CompareSnapshotsTab } from '../content/governance';
import { AssessmentPoolHealthTab } from '../content/governance/tabs/AssessmentPoolHealthTab';
import { PracticeContentHealthTab } from '../content/governance/tabs/PracticeContentHealthTab';
import { SaveSnapshotModal } from '../content/governance/tabs/SaveSnapshotModal';
import type { AdminNavigationContext, AdminRoute } from '../adminNavigation';
import { readAllowedSearchParam, readSearchParam, replaceSearchParams } from '../adminUrlState';
import { isVisualPolicyDegraded } from '../../../governance/pipeline/policyEngine';
import { createSnapshot, saveSnapshot } from '../../../governance/pipeline/snapshots';
// ─── Types & Constants ────────────────────────────────────────────────────────
type TabId = 'overzicht' | 'vragen' | 'vergelijken' | 'toetsen' | 'oefenstof' | 'regels' | 'instellingen';
const TABS = [{
  id: 'overzicht',
  label: 'Overzicht',
  icon: <Activity className="w-4 h-4" data-id="element-7008" data-mp="gp8sbh5ezgz3" />
}, {
  id: 'vragen',
  label: 'Vragen',
  icon: <Table2 className="w-4 h-4" data-id="element-7009" data-mp="lolmkmym7qbp" />
}, {
  id: 'vergelijken',
  label: 'Vergelijken',
  icon: <GitCompare className="w-4 h-4" data-id="element-7010" data-mp="qnom5rvtt4ca" />
}, {
  id: 'toetsen',
  label: 'Toetsen',
  icon: <GraduationCap className="w-4 h-4" data-id="element-7011" data-mp="2nfyi4lep0lv3" />
}, {
  id: 'oefenstof',
  label: 'Oefenstof',
  icon: <BookOpenCheck className="w-4 h-4" data-id="element-7012" data-mp="2sf1jnvvgs9yb" />
}, {
  id: 'regels',
  label: 'Regels',
  icon: <ShieldCheck className="w-4 h-4" data-id="element-7013" data-mp="2kedj80ukpi5w" />
}, {
  id: 'instellingen',
  label: 'Instellingen',
  icon: <Sliders className="w-4 h-4" data-id="element-7014" data-mp="2ffioc0jc035f" />
}] as const;
const TAB_IDS = TABS.map(tab => tab.id) as readonly TabId[];
const TAB_DESCRIPTIONS: Record<TabId, string> = {
  overzicht: 'Systeemgezondheid, top blockers en module status op een oogopslag.',
  vragen: 'Eén centrale tabel met alle vragen, validatieresultaten, quality scores en seed issues.',
  vergelijken: 'Vergelijk twee pipeline runs om regressies of verbeteringen te spotten.',
  toetsen: 'Toets-feasibility per groep × module: kan de blueprint-generator een volledige Cito-style toets opleveren?',
  oefenstof: 'Oefenstof-capaciteit per groep en family: waar dreigen herhaling, fallback of leerdoel-drift?',
  regels: 'Alle validatieregels, regelboeken en drempelwaarden — de complete governance-referentie.',
  instellingen: 'Configureer kwaliteitspoorten, pipeline-fasen en beleidsregels.'
};
// ─── Health Status ────────────────────────────────────────────────────────────
const HEALTH_CONFIG = {
  pending: {
    label: 'Niet uitgevoerd',
    color: 'text-gray-600',
    bg: 'bg-gray-50',
    border: 'border-gray-200',
    dot: 'bg-gray-400'
  },
  ok: {
    label: 'Gezond',
    color: 'text-emerald-700',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    dot: 'bg-emerald-500'
  },
  warning: {
    label: 'Aandacht vereist',
    color: 'text-amber-700',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    dot: 'bg-amber-500'
  },
  error: {
    label: 'Kritieke fouten',
    color: 'text-red-700',
    bg: 'bg-red-50',
    border: 'border-red-200',
    dot: 'bg-red-500'
  }
};
function getHealthStatus(fail: number, review: number, isRunning: boolean) {
  if (isRunning) return 'pending';
  if (fail > 0) return 'error';
  if (review > 0) return 'warning';
  return 'ok';
}
// ─── Main Page Component ──────────────────────────────────────────────────────
interface GovernancePageProps {
  navigationContext?: AdminNavigationContext | null;
  onNavigate?: (route: AdminRoute, context?: AdminNavigationContext) => void;
}
export function GovernancePage({
  navigationContext,
  onNavigate
}: GovernancePageProps) {
  const readUrlState = useCallback(() => {
    const filter = readSearchParam('filter');
    return {
      activeTab: filter ? 'vragen' : readAllowedSearchParam('tab', TAB_IDS, 'overzicht'),
      questionFilter: filter
    };
  }, []);
  const [activeTab, setActiveTab] = useState<TabId>(() => readUrlState().activeTab);
  const [questionFilter, setQuestionFilter] = useState<string | undefined>(() => readUrlState().questionFilter);
  const [isSnapshotModalOpen, setIsSnapshotModalOpen] = useState(false);
  const [snapshotRefreshTrigger, setSnapshotRefreshTrigger] = useState(0);
  // R8 (Sprint 5): surface degraded visual-policy state so admins know that
  // visual answer-leakage checks may be running on the empty default policy.
  const visualPolicyDegraded = isVisualPolicyDegraded();
  const {
    rows,
    stats,
    systemStats,
    moduleHealth,
    familyHealthByModule,
    questionTypeDistribution,
    visualTypeDistribution,
    topBlockers,
    familyTriage,
    assessmentPoolHealth,
    practiceContentHealth,
    pipelineIssueCounts,
    isRunning,
    lastRun,
    batchResult,
    revalidate,
    shadowImpactReport
  } = useGovernanceData();
  const overallHealth = getHealthStatus(stats.fail, stats.review, isRunning);
  const hcfg = HEALTH_CONFIG[overallHealth];
  const applyQuestionContext = useCallback((filter?: string) => {
    setQuestionFilter(filter);
    setActiveTab('vragen');
    replaceSearchParams({
      tab: 'vragen',
      filter
    });
  }, []);
  useEffect(() => {
    const handlePopState = () => {
      const nextState = readUrlState();
      setActiveTab(nextState.activeTab);
      setQuestionFilter(nextState.questionFilter);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [readUrlState]);
  useEffect(() => {
    if (navigationContext?.kind !== 'governance-question') return;
    applyQuestionContext(navigationContext.questionId);
  }, [applyQuestionContext, navigationContext]);
  const handleNavigateToQuestion = (filter?: string) => {
    applyQuestionContext(filter);
  };
  const handleTabChange = (id: string) => {
    const nextTab = id as TabId;
    const nextFilter = nextTab === 'vragen' ? questionFilter : undefined;
    setActiveTab(nextTab);
    setQuestionFilter(nextFilter);
    replaceSearchParams({
      tab: nextTab,
      filter: nextFilter
    });
  };
  const handleSaveSnapshot = (label: string, note: string) => {
    if (!batchResult) return;
    const snap = createSnapshot(batchResult, {
      label: label || undefined,
      note: note || undefined
    });
    const success = saveSnapshot(snap);
    if (success) {
      setSnapshotRefreshTrigger(prev => prev + 1);
      // In a real app we'd use a toast here
      alert('Snapshot succesvol opgeslagen!');
    } else {
      alert('Opslaan mislukt — localStorage vol?');
    }
  };
  return <PageLayout title="Governance & Kwaliteit" subtitle="Kwaliteitsbeheer, validatie en analyse van het vragenbestand" actions={<div className="flex items-center gap-3" data-id="element-7016" data-mp="1myftxc0xu0p0">
          {/* Health Status Indicator */}
          <div className={`hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg border ${hcfg.border} ${hcfg.bg}`} data-id="element-7017" data-mp="2ao0e1y8tv3zv">
            <div className={`w-2 h-2 rounded-full ${hcfg.dot} ${overallHealth !== 'pending' ? 'animate-pulse' : ''}`} data-id="element-7018" data-mp="2eplo7btz946d" />
            <span className={`text-xs font-medium ${hcfg.color}`} data-id="element-7019" data-mp="25p6y7z62j0wj">
              {hcfg.label}
            </span>
          </div>

          <Button variant="secondary" onClick={() => setIsSnapshotModalOpen(true)} disabled={isRunning || !batchResult} data-id="element-7020" data-mp="pa5p6p9royu7">
            <Camera className="w-4 h-4 mr-2" data-id="element-7021" data-mp="1lxldw88l8r8h" />
            Snapshot opslaan
          </Button>

          {/* Re-validate Button */}
          <Button variant="primary" onClick={revalidate} disabled={isRunning} data-id="element-7022" data-mp="1gxqjhupltjio">
            {isRunning ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" data-id="element-7023" data-mp="1c0golzjg4fng" /> : <RefreshCw className="w-4 h-4 mr-2" data-id="element-7024" data-mp="171nl8zxvi510" />}
            {isRunning ? 'Valideren...' : 'Hervalideer'}
          </Button>

          <ConnectionBadge status="live" data-id="element-7025" data-mp="24qfh64wumuwv" />
        </div>} breadcrumbs={[{
    label: 'Inhoud'
  }, {
    label: 'Governance & Kwaliteit'
  }]} data-id="element-7015" data-mp="1vskfph17slxo">
      {/* R8: Visual-policy degraded banner */}
      {visualPolicyDegraded && <div role="alert" className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4 mb-4" data-id="element-7026" data-mp="1pz6n9jlwyndx">
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" data-id="element-7027" data-mp="2inlydv1xcljz" />
          <div className="text-sm" data-id="element-7028" data-mp="2qwrpm99btabd">
            <p className="font-semibold text-red-800" data-id="element-7029" data-mp="2qxtd5d7j3p9z">
              Visuele policy gedegradeerd
            </p>
            <p className="text-red-700 mt-1" data-id="element-7030" data-mp="2uz4vcxo2p885">
              De visual policy kon niet uit de seeds worden geladen (build
              mislukte 3×). Visual-stage answer-leakage checks draaien tegen de
              lege default policy — visual-issues kunnen onvolledig zijn. Een
              app-restart kan dit oplossen.
            </p>
          </div>
        </div>}

      {/* Health Banner (Mobile) */}
      <div className={`sm:hidden flex items-center justify-between gap-3 rounded-xl border ${hcfg.border} ${hcfg.bg} p-3 mb-3`} data-id="element-7031" data-mp="3dvo4hmyvmw4p">
        <div className="flex items-center gap-2" data-id="element-7032" data-mp="3nmr6qs3jlh5k">
          <div className={`w-2.5 h-2.5 rounded-full ${hcfg.dot} ${overallHealth !== 'pending' ? 'animate-pulse' : ''}`} data-id="element-7033" data-mp="3nmiwz92puryl" />
          <span className={`text-sm font-medium ${hcfg.color}`} data-id="element-7034" data-mp="1fkma7nsdgel">
            {hcfg.label}
          </span>
        </div>
        {lastRun && <span className="text-xs text-gray-500" data-id="element-7035" data-mp="16s1mk2lysp4w">{lastRun}</span>}
      </div>

      <AdminTabs tabs={TABS} activeTab={activeTab} onTabChange={handleTabChange} data-id="element-7036" data-mp="1as3qyjj33wnv" />

      <p className="text-sm text-gray-500 mb-5" data-id="element-7037" data-mp="1frgaovensnm8">
        {TAB_DESCRIPTIONS[activeTab]}
      </p>

      <div data-id="element-7038" data-mp="1kiokvtntcd0g">
        {activeTab === 'overzicht' && <OverviewDashboard rows={rows} stats={stats} systemStats={systemStats} moduleHealth={moduleHealth} familyHealthByModule={familyHealthByModule} questionTypeDistribution={questionTypeDistribution} visualTypeDistribution={visualTypeDistribution} topBlockers={topBlockers} familyTriage={familyTriage} pipelineIssueCounts={pipelineIssueCounts} shadowImpactReport={shadowImpactReport} isRunning={isRunning} onNavigateToQuestion={handleNavigateToQuestion} data-id="element-7039" data-mp="1kk9ixt9c6e32" />}
        {activeTab === 'vragen' && <QuestionsTab rows={rows} batchResult={batchResult} isRunning={isRunning} initialFilter={questionFilter} onOpenQuestionInBank={row => onNavigate?.('question-bank', {
        kind: 'question-bank-question',
        familyId: row.familyId,
        questionId: row.questionId
      })} data-id="element-7040" data-mp="1lbo5d385ofb" />}
        {activeTab === 'vergelijken' && <CompareSnapshotsTab refreshTrigger={snapshotRefreshTrigger} data-id="element-7041" data-mp="fkjkly806ift" />}
        {activeTab === 'toetsen' && <AssessmentPoolHealthTab report={assessmentPoolHealth} isRunning={isRunning} onSelectQuestion={handleNavigateToQuestion} data-id="element-7042" data-mp="ailx2rskl1h2" />}
        {activeTab === 'oefenstof' && <PracticeContentHealthTab report={practiceContentHealth} isRunning={isRunning} data-id="element-7043" data-mp="2r92glvil4hc3" />}
        {activeTab === 'regels' && <RulesTab data-id="element-7044" data-mp="2sqrcenw96w77" />}
        {activeTab === 'instellingen' && <GovernanceSettingsTab data-id="element-7045" data-mp="2xpuk6g3mbgiv" />}
      </div>

      <SaveSnapshotModal isOpen={isSnapshotModalOpen} onClose={() => setIsSnapshotModalOpen(false)} onSave={handleSaveSnapshot} data-id="element-7046" data-mp="2xosxtfh5j7lu" />
    </PageLayout>;
}